/**
 * Represents an image component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array} [props.url=null] - The array of image sources for different screen sizes. if image doesn't have src set, we can using image src instead
 * @param {string} [props.alt=''] - The alternative text for the image.
 * @param {string} [props.className=''] - The additional CSS class for the component.
 * @param {string} [props.imageClass=''] - The additional CSS class for the image element.
 * @param {boolean} [props.isLazy=true] - Determines if lazy loading is enabled for the image.
 * @param {boolean} [props.isCover=true] - Determines if the image should be displayed as a cover or contain.
 * @returns {JSX.Element} The rendered image component.
 */

import { useEffect } from 'preact/hooks'

function Image({
  url = null,
  alt = '',
  width = null,
  height = null,
  sizes = [180, 320, 480, 640, 768, 1080, 1440, 2160, 2880],
  isLazy = true,
  isCover = true,
  loading = 'lazy',
  className = '',
  imageClass = '',
  classPictureWrap = ''
}) {
  useEffect(() => {
    let lazySizesInstance

    if (window.lazySizes) {
      lazySizesInstance = window.lazySizes.init()
    }

    return () => {
      if (lazySizesInstance) {
        lazySizesInstance.destroy()
      }
    }
  }, [])

  const getSrcset = (imageUrl) => {
    if (!imageUrl) {
      return GM_STATE.shopify.defaultImage
    }

    if (!width) {
      return imageUrl
    }

    return sizes
      .reduce((acc, size) => {
        if (size <= width) {
          acc.push(`${imageUrl}&width=${size} ${size}w`)
        }

        return acc
      }, [])
      .join(',')
  }

  const srcsetString = getSrcset(url)

  return (
    <figure className={`relative ${className}`}>
      <picture className={classPictureWrap}>
        {isLazy ? (
          <source media="(min-width: 0px)" data-srcset={srcsetString} />
        ) : (
          <source media="(min-width: 0px)" srcSet={srcsetString} />
        )}
        <img
          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUCB1j+Pjx438ACX0D04PrXz0AAAAASUVORK5CYII="
          className={[isCover ? 'object-cover' : 'object-contain', isLazy ? 'lazyload' : '', imageClass].join(' ')}
          data-sizes="auto"
          data-src={url}
          width={width}
          height={height}
          alt={alt}
          fetchPriority={isLazy ? 'auto' : 'high'}
          loading={loading}
        />
      </picture>
    </figure>
  )
}

export default Image
